import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Textarea } from '@heroui/input';
import { Select, SelectItem } from '@/components/ui/AppSelect';
import { Button } from '@heroui/react';
import { AuthOnly } from '@/contexts/auth-context';
import { useMutation } from '@tanstack/react-query';
import { createEvent, createEventWithImages } from '@/apiEndpoints';
import { useAuth } from '@/contexts/auth-context';
import { DatePicker } from '@heroui/date-picker';
import { addToast } from '@heroui/toast';
import { hasAspectRatio, BANNER_RATIO_ERROR } from '@/utils/imageRatio';
import { TimeInput } from '@heroui/date-input';
import { getLocalTimeZone, today, type DateValue } from '@internationalized/date';
import { GoogleMap, Marker, useJsApiLoader, Autocomplete } from '@react-google-maps/api';
import Calendar from '@/icons/Calendar';
import Time from '@/icons/Time';
import UploadImagePlaceholder from '@/components/UploadImagePlaceholder';
import { PageHero, btnAccent, btnGhost, card, label as labelCls, pageBg, wrap } from '@/components/ui/design';

const fieldWrap = 'h-14 rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const areaWrap = 'rounded-2xl bg-grey99 shadow-none ring-1 ring-black/[0.04] data-[hover=true]:bg-grey95 group-data-[focus=true]:bg-grey99 group-data-[focus=true]:ring-2 group-data-[focus=true]:ring-primary/50 dark:bg-white/[0.06] dark:ring-white/10 dark:data-[hover=true]:bg-white/10 dark:group-data-[focus=true]:bg-white/[0.06] dark:group-data-[focus=true]:ring-nightAccent/60';
const fieldText = 'text-base text-grey10 placeholder:text-grey60 dark:text-white dark:placeholder:text-nightMuted';
const dropZone = 'flex w-full cursor-pointer flex-col items-center justify-center gap-2 overflow-hidden rounded-[24px] border-2 border-dashed border-grey90 bg-grey99 transition-colors hover:border-primary/40 dark:border-white/10 dark:bg-white/[0.04] dark:hover:border-nightAccent/40';
const groupTitle = 'mb-4 text-xl font-bold tracking-[-0.02em] text-grey10 dark:text-white';
const hint = 'font-medium text-grey50 dark:text-nightMuted';

const steps = [
    { label: 'Basic info' },
    { label: 'Where and When' },
];

export default function PromoteEvent() {
    const navigate = useNavigate();
    const [step, setStep] = useState(0);
    const [eventLink, setEventLink] = useState('');
    // Refactored image states
    const [banner, setBanner] = useState<File | null>(null);
    const [bannerRatioError, setBannerRatioError] = useState('');
    const [mainSocialImage, setMainSocialImage] = useState<File | null>(null);
    const [additionalSocialImages, setAdditionalSocialImages] = useState<File[]>([]);
    const [title, setTitle] = useState('');
    const [category, setCategory] = useState('');
    const [desc, setDesc] = useState('');
    const [location, setLocation] = useState('');
    const [price, setPrice] = useState('');
    const [discountedPrice, setDiscountedPrice] = useState('');
    const [coupon, setCoupon] = useState('');
    // Replace string states for dates/times with DateValue/TimeValue
    const [salesStartDate, setSalesStartDate] = useState<DateValue | null>(null);
    const [salesStartTime, setSalesStartTime] = useState<any>(null);
    const [salesEndDate, setSalesEndDate] = useState<DateValue | null>(null);
    const [salesEndTime, setSalesEndTime] = useState<any>(null);
    const [eventStartDate, setEventStartDate] = useState<DateValue | null>(null);
    const [eventStartTime, setEventStartTime] = useState<any>(null);
    const [eventEndDate, setEventEndDate] = useState<DateValue | null>(null);
    const [eventEndTime, setEventEndTime] = useState<any>(null);
    const [address, setAddress] = useState('');
    const [venue, setVenue] = useState('');
    const [city, setCity] = useState('');
    const [country, setCountry] = useState('');
    const [state, setState] = useState('');
    const [zipcode, setZipcode] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const { user } = useAuth();
    const [lat, setLat] = useState(34.0522); // Default: Los Angeles
    const [lng, setLng] = useState(-118.2437);
    const [autocomplete, setAutocomplete] = useState<any>(null);
    const [map, setMap] = useState<any>(null);
    const mapContainerStyle = { width: '100%', height: '300px', borderRadius: '16px' };
    const { isLoaded } = useJsApiLoader({
        googleMapsApiKey: 'AIzaSyBKbmD1FAPDpYvIFHjUJpeZ9XbohnH_QFI',
        libraries: ['places'],
    });
    const [addressLoading, setAddressLoading] = useState(false);

    // Helper function to extract complete address object from Google address components
    function extractAddressFromComponents(addressComponents: any[]): { country: string; state: string; city: string; zipcode: string; fullAddress: string } {
        if (!addressComponents || !Array.isArray(addressComponents)) {
            return { country: '', state: '', city: '', zipcode: '', fullAddress: '' };
        }

        const address: { country: string; state: string; city: string; zipcode: string; fullAddress: string } = {
            country: '',
            state: '',
            city: '',
            zipcode: '',
            fullAddress: ''
        };

        // Extract country
        const country = addressComponents.find(component =>
            component.types.includes('country')
        );
        if (country) address.country = country.long_name;

        // Extract state/province
        const state = addressComponents.find(component =>
            component.types.includes('administrative_area_level_1')
        );
        if (state) address.state = state.long_name;

        // Extract city
        const city = addressComponents.find(component =>
            component.types.includes('locality')
        );
        if (city) address.city = city.long_name;

        // Extract postal code
        const zipcode = addressComponents.find(component =>
            component.types.includes('postal_code')
        );
        if (zipcode) address.zipcode = zipcode.long_name;

        return address;
    }

    function onLoadAutocomplete(ac: any) {
        setAutocomplete(ac);
    }
    function onPlaceChanged() {
        if (autocomplete) {
            const place = autocomplete.getPlace();
            if (place.geometry && place.geometry.location) {
                const newLat = place.geometry.location.lat();
                const newLng = place.geometry.location.lng();
                setLat(newLat);
                setLng(newLng);

                // Extract complete address from address components
                const addressData = extractAddressFromComponents(place.address_components);

                setAddress(place.formatted_address || '');
                setCity(addressData.city);
                setState(addressData.state);
                setCountry(addressData.country);
                setZipcode(addressData.zipcode);

                if (map) {
                    map.panTo({ lat: newLat, lng: newLng });
                }
            }
        }
    }
    function onMapLoad(mapInstance: any) {
        setMap(mapInstance);
    }
    async function fetchAddressFromCoords(lat: number, lng: number) {
        setAddressLoading(true);
        try {
            const response = await fetch(
                `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyBTl2hQB_YKIYPeWLD2qJ0G08yTo7SErP8`
            );
            const data = await response.json();
            if (data.results && data.results[0]) {
                const result = data.results[0];
                // Extract complete address from address components
                const addressData = extractAddressFromComponents(result.address_components);

                setAddress(result.formatted_address);
                setCity(addressData.city);
                setState(addressData.state);
                setCountry(addressData.country);
                setZipcode(addressData.zipcode);
            }
        } catch (e) {
            // Optionally handle error
        } finally {
            setAddressLoading(false);
        }
    }

    const mutation = useMutation({
        mutationFn: async (data: any) => {
            return createEventWithImages(data);
        },
        onSuccess: (res) => {
            setSuccess(true);
            setError('');
            setLoading(false);
            // Redirect to the created event page
            const eventId = res?.data?._id || res?.data?.id || res?._id || res?.id;
            if (eventId) {
                setTimeout(() => {
                    navigate(`/promoted-event/${eventId}`);
                }, 1500);
            } else {
                // Fallback to home page if no event ID
                setTimeout(() => {
                    navigate('/');
                }, 1500);
            }
        },
        onError: (err: any) => {
            setError(err?.message || 'Failed to create event');
            setSuccess(false);
            setLoading(false);
        },
    });

    // Refactored image handlers
    async function handleBannerChange(e: React.ChangeEvent<HTMLInputElement>) {
        const files = e.target.files;
        if (!files) return;
        const file = files[0] || null;
        e.target.value = '';
        if (file && !(await hasAspectRatio(file))) {
            setBannerRatioError(BANNER_RATIO_ERROR);
            addToast({ title: 'Invalid image', description: BANNER_RATIO_ERROR, color: 'danger' });
            return;
        }
        setBannerRatioError('');
        setBanner(file);
    }
    function handleMainSocialImageChange(e: React.ChangeEvent<HTMLInputElement>) {
        const files = e.target.files;
        if (!files) return;
        setMainSocialImage(files[0] || null);
    }
    function handleAdditionalSocialImagesChange(e: React.ChangeEvent<HTMLInputElement>) {
        const files = e.target.files;
        if (!files) return;
        setAdditionalSocialImages(prev => [...prev, ...Array.from(files)]);
    }

    function validateFields() {
        if (!title || !desc || !category) return 'Please fill all required fields in Basic Info.';
        if (!price) return 'Price is required.';
        //if (!lat) return 'Location is required.';
        if (!address) return 'Address is required.';
        //if (!venue) return 'Venue is required.';
        if (!salesStartDate || !salesStartTime || !salesEndDate || !salesEndTime) return 'Please fill all sales open/close fields.';
        if (!eventStartDate || !eventStartTime || !eventEndDate || !eventEndTime) return 'Please fill all event start/end fields.';
        if (!lat || !lng) return 'Please select a location on the map.';

        // Date time validation logic
        const salesStartDateTime = toISO(salesStartDate, salesStartTime);
        const salesEndDateTime = toISO(salesEndDate, salesEndTime);
        const eventStartDateTime = toISO(eventStartDate, eventStartTime);
        const eventEndDateTime = toISO(eventEndDate, eventEndTime);

        // Validate sales end is after sales start
        if (salesStartDateTime && salesEndDateTime && new Date(salesStartDateTime) >= new Date(salesEndDateTime)) {
            return 'Sales end date/time must be after sales start date/time.';
        }

        // Validate event end is after event start
        if (eventStartDateTime && eventEndDateTime && new Date(eventStartDateTime) >= new Date(eventEndDateTime)) {
            return 'Event end date/time must be after event start date/time.';
        }

        // Validate sales dates are before event dates
        if (salesEndDateTime && eventStartDateTime && new Date(salesEndDateTime) >= new Date(eventStartDateTime)) {
            return 'Sales must end before the event starts.';
        }

        // Validate that sales start is not in the past (optional - you can remove this if you want to allow past dates)
        const now = new Date();
        if (salesStartDateTime && new Date(salesStartDateTime) < now) {
            return 'Sales start date/time cannot be in the past.';
        }

        return '';
    }

    // Helper to format time object to 'HH:mm' string
    function formatTimeObjToString(timeObj: any) {
        if (!timeObj || typeof timeObj.hour !== 'number' || typeof timeObj.minute !== 'number') return '';
        const hh = String(timeObj.hour).padStart(2, '0');
        const mm = String(timeObj.minute).padStart(2, '0');
        return `${hh}:${mm}`;
    }
    // Helper to format date object to 'YYYY-MM-DD' string
    function formatDateObjToString(dateObj: any) {
        if (!dateObj || typeof dateObj.toString !== 'function') return '';
        return dateObj.toString();
    }

    function toISO(date: DateValue | null, time: any) {
        if (!date || !time) return '';
        // Combine date and time into ISO string
        return `${formatDateObjToString(date)}T${formatTimeObjToString(time)}`;
    }

    function handlePublish() {
        // Prevent multiple publish actions
        if (mutation.isPending) {
            return;
        }

        setError('');
        setSuccess(false);
        const validationError = validateFields();
        if (validationError) {
            setError(validationError);
            return;
        }
        setLoading(true);
        let t1 = `${formatDateObjToString(eventStartDate)} ${formatTimeObjToString(eventStartTime)}`.trim();
        let t2 = `${formatDateObjToString(eventEndDate)} ${formatTimeObjToString(eventEndTime)}`.trim();
        const times = [
            {
                startDate: t1,
                endDate: t2,
            },
        ];



        // Prepare form data if needed for file uploads
        const formData = new FormData();
        formData.append('title', title.toString());
        formData.append('description', desc.toString());
        formData.append('category', category.toString());
        formData.append('times', JSON.stringify(times));
        // Create complete address object
        const addressObject = {
            fullAddress: address,
            country: country,
            state: state,
            city: city,
            zipcode: zipcode
        };
        formData.append('address', JSON.stringify(addressObject));
        formData.append('venue', '');
        formData.append('eventType', 'promoted');
        formData.append('price', price);
        formData.append('promotedSellStart', toISO(salesStartDate, salesStartTime));
        formData.append('promotedSellEnd', toISO(salesEndDate, salesEndTime));
        formData.append('promotedCouponCode', coupon);
        formData.append('promotedDiscountedPrice', discountedPrice);
        formData.append('promotedEventLink', eventLink);
        formData.append('userId', user?.userId || '');
        formData.append('lat', lat.toString());
        formData.append('lng', lng.toString());
        if (banner) formData.append('banner', banner);
        if (mainSocialImage) formData.append('images', mainSocialImage);
        additionalSocialImages.forEach(file => formData.append('images', file));
        mutation.mutate(formData);
    }

    // Remove fetchAddressFromCoords from onIdle, and use useEffect for lat/lng changes
    useEffect(() => {
        if (step === 1 && isLoaded && lat && lng) {
            fetchAddressFromCoords(lat, lng);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [lat, lng, step, isLoaded]);

    return (
        <AuthOnly>
            <>
                <div className={pageBg}>
                <Header />
                <PageHero
                    eyebrow="Promote an event"
                    title="Spread the Word ✨"
                    subtitle="Share events and spark excitement!"
                >
                    <div className="mt-8 flex flex-wrap items-center gap-2">
                        {steps.map((s, i) => (
                            <span
                                key={s.label}
                                className={`inline-flex h-10 items-center gap-2 rounded-full px-4 text-sm font-semibold transition-colors ${i === step
                                    ? 'bg-grey10 text-white dark:bg-white dark:text-nightBg'
                                    : 'bg-grey99 text-grey40 ring-1 ring-black/[0.04] dark:bg-white/[0.06] dark:text-nightMuted dark:ring-white/[0.06]'}`}
                            >
                                <span className={`flex h-5 w-5 items-center justify-center rounded-full text-xs ${i === step ? 'bg-primary text-white dark:bg-nightAccent dark:text-nightBg' : 'bg-white text-grey40 dark:bg-white/10 dark:text-nightMuted'}`}>{i + 1}</span>
                                {s.label}
                            </span>
                        ))}
                    </div>
                </PageHero>
                <div className={`${wrap} min-h-[70vh] pb-16 pt-10 sm:pb-24`}>
                    {/* Step Content */}
                    {step === 0 && (
                        <div className={`${card} p-5 sm:p-8`}>
                            <div className="mb-6">
                                <label className={labelCls}>Event link <span className={`text-xs ${hint}`}>(If you don’t have a link leave this field empty)</span></label>
                                <Input classNames={{ inputWrapper: fieldWrap, input: fieldText }} className="w-full" placeholder="e.g. https://site.com/eventpage..." value={eventLink} onChange={e => setEventLink(e.target.value)} />
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div className="md:col-span-2">
                                    <label className={labelCls}>Event page banner photo <span className={`text-xs ${hint}`}>(16:9, Min 1024 x 576, Max 5MB)</span></label>
                                    <label className={`${dropZone} aspect-video`}>
                                        <input type="file" accept="image/*" className="hidden" onChange={handleBannerChange} />
                                        {banner ? (
                                            <img src={URL.createObjectURL(banner)} alt="Banner preview" className="h-full w-full object-cover" />
                                        ) : (
                                            <>
                                                <UploadImagePlaceholder />
                                                <span className="text-sm font-semibold text-primary dark:text-nightAccent">Upload Image</span>
                                            </>
                                        )}
                                    </label>
                                    {bannerRatioError && <p className="mt-1.5 text-xs font-medium text-red-500 dark:text-red-400">{bannerRatioError}</p>}
                                </div>
                                <div>
                                    <label className={labelCls}>Social media image <span className={`text-xs ${hint}`}>(Min 400 x 400, Max 2MB)</span></label>
                                    <label className={`${dropZone} h-[280px] md:h-auto md:aspect-[4/5] lg:h-[352px] lg:aspect-auto`}>
                                        <input type="file" accept="image/*" className="hidden" onChange={handleMainSocialImageChange} />
                                        {mainSocialImage ? (
                                            <img src={URL.createObjectURL(mainSocialImage)} alt="Social preview" className="h-full w-full object-cover" />
                                        ) : (
                                            <>
                                                <UploadImagePlaceholder />
                                                <span className="text-sm font-semibold text-primary dark:text-nightAccent">Upload Image</span>
                                            </>
                                        )}
                                    </label>
                                    <input type="file" accept="image/*" multiple className="hidden" id="additional-social-upload" onChange={handleAdditionalSocialImagesChange} />
                                    {/* <label htmlFor="additional-social-upload" className="text-sm font-bold text-primary40 mt-2 cursor-pointer">+ Add additional images</label> */}
                                    {additionalSocialImages.length > 0 && (
                                        <div className="flex flex-wrap gap-2 mt-2">
                                            {additionalSocialImages.map((file, idx) => (
                                                <div key={idx} className="relative">
                                                    <img
                                                        src={URL.createObjectURL(file)}
                                                        alt={`Additional image ${idx + 1}`}
                                                        className="h-16 w-16 rounded-2xl object-cover ring-1 ring-black/[0.06] dark:ring-white/10"
                                                    />
                                                    <button
                                                        onClick={() => setAdditionalSocialImages(prev => prev.filter((_, i) => i !== idx))}
                                                        className="absolute -right-1.5 -top-1.5 flex h-6 w-6 items-center justify-center rounded-full bg-grey10 text-xs text-white ring-2 ring-white hover:bg-black dark:bg-white dark:text-nightBg dark:ring-nightBg"
                                                        title="Remove image"
                                                    >
                                                        ×
                                                    </button>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div className="md:col-span-2">
                                    <label className={labelCls}>Event title: <span className="text-red-500">*</span></label>
                                    <Input classNames={{ inputWrapper: fieldWrap, input: fieldText }} className="mb-2 w-full" placeholder="e.g. Summer Soirée: A Night of Music and Fun" value={title} onChange={e => setTitle(e.target.value)} />
                                </div>
                                <div>
                                    <label className={labelCls}>Select event category: <span className="text-red-500">*</span></label>
                                    <Select aria-label="Select a category"
                                        classNames={{ trigger: fieldWrap, value: fieldText }}
                                        className="w-full mb-2"
                                        selectedKeys={[category]}
                                        onSelectionChange={value => {
                                            if (typeof value.currentKey === 'string') setCategory(value.currentKey);
                                        }}
                                        placeholder="Select a category"
                                        items={[
                                            { key: 'networking', label: 'Networking' },
                                            { key: 'business', label: 'Business' },
                                            { key: 'music', label: 'Music' },
                                            { key: 'art', label: 'Arts & Culture' },
                                            { key: 'social', label: 'Social & Community' },
                                            { key: 'health', label: 'Health & Wellness' },
                                            { key: 'sports', label: 'Sports & Fitness' },
                                            { key: 'science', label: 'Science & Tech' },
                                        ]}
                                    >
                                        {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
                                    </Select>
                                </div>
                            </div>
                            <div className="mb-6">
                                <label className={labelCls}>About this event: <span className="text-red-500">*</span></label>
                                <Textarea classNames={{ inputWrapper: areaWrap, input: fieldText }} className="w-full" rows={3} placeholder="Tell others why this event worth sharing" value={desc} onChange={e => setDesc(e.target.value)} />
                            </div>
                            <div className="flex justify-end">
                                <Button className={`${btnAccent} w-full sm:w-auto`} onClick={() => setStep(1)}>
                                    <span className="block text-[15px] font-bold sm:hidden">Continue &rarr;</span>
                                    <span className="hidden text-[15px] font-bold sm:block">Continue To Where And When &rarr;</span>
                                </Button>
                            </div>
                        </div>
                    )}
                    {step === 1 && (
                        <div className={`${card} p-5 sm:p-8`}>
                            {/* Map Picker and Autocomplete */}
                            <div className="mb-6">
                                <label className={labelCls}>Search for event location:</label>
                                {isLoaded && (
                                    <div>
                                        <Autocomplete onLoad={onLoadAutocomplete} onPlaceChanged={onPlaceChanged}>
                                            <Input classNames={{ inputWrapper: fieldWrap, input: fieldText }} className="mb-2 w-full" placeholder="Search for a place or address"
                                                value={addressLoading ? 'Loading address...' : address}
                                                onChange={e => setAddress(e.target.value)} />
                                        </Autocomplete>
                                        <div style={{ position: 'relative' }} className="overflow-hidden rounded-[24px] ring-1 ring-black/[0.06] dark:ring-white/10">
                                            <GoogleMap
                                                mapContainerStyle={mapContainerStyle}
                                                center={{ lat, lng }} // Added center prop
                                                zoom={14}
                                                onLoad={onMapLoad}
                                                onIdle={() => {
                                                    if (map) {
                                                        const center = map.getCenter();
                                                        if (center) { // Added null check
                                                            setLat(center.lat());
                                                            setLng(center.lng());
                                                            // fetchAddressFromCoords(center.lat(), center.lng()); // Removed from here
                                                        }
                                                    }
                                                }}
                                            />
                                            {/* Custom marker icon centered over the map */}
                                            <div style={{
                                                position: 'absolute',
                                                left: '50%',
                                                top: '50%',
                                                transform: 'translate(-50%, -100%)',
                                                pointerEvents: 'none',
                                                zIndex: 10
                                            }}>
                                                <img src="/marker.png" />
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
                                <div>
                                    <label className={labelCls}>Price: <span className="text-red-500">*</span></label>
                                    <Input classNames={{ inputWrapper: fieldWrap, input: fieldText }} className="w-full" placeholder="$ Price" value={price} onChange={e => setPrice(e.target.value)} />
                                </div>
                                <div>
                                    <label className={labelCls}>Discounted price:</label>
                                    <Input classNames={{ inputWrapper: fieldWrap, input: fieldText }} className="w-full" placeholder="$ Price" value={discountedPrice} onChange={e => setDiscountedPrice(e.target.value)} />
                                </div>
                                <div>
                                    <label className={labelCls}>Coupon code</label>
                                    <Input classNames={{ inputWrapper: fieldWrap, input: fieldText }} className="w-full" placeholder="Enter code here" value={coupon} onChange={e => setCoupon(e.target.value)} />
                                </div>
                            </div>
                            {/* Event Start & End Time Fields */}
                            <div className="mb-8 border-t border-black/[0.06] pt-8 dark:border-white/10">
                                <label className={`block ${groupTitle}`}>Event Start & End:</label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className={labelCls}>Event starts on: <span className="text-red-500">*</span></label>
                                        <div className="flex flex-col gap-2 sm:flex-row">
                                            <DatePicker classNames={{ inputWrapper: fieldWrap, input: fieldText }} endContent={<Calendar />}
                                                minValue={today(getLocalTimeZone())}
                                                visibleMonths={2}
                                                className="w-full" value={eventStartDate} onChange={(newDate) => {
                                                    setEventStartDate(newDate);
                                                    if (newDate && eventEndDate && eventEndDate.compare(newDate) < 0) {
                                                        setEventEndDate(newDate);
                                                    }
                                                }} />
                                            <TimeInput classNames={{ inputWrapper: fieldWrap, input: fieldText }} endContent={<Time />}
                                                hourCycle={12}
                                                className="w-full" value={eventStartTime} onChange={(newTime) => {
                                                    setEventStartTime(newTime);
                                                    // If same date and end time is now before start time, clear end time
                                                    /* if (eventStartDate && eventEndDate && eventStartDate.toString() === eventEndDate.toString() && newTime && eventEndTime) {
                                                        const startMinutes = newTime.hour * 60 + newTime.minute;
                                                        const endMinutes = eventEndTime.hour * 60 + eventEndTime.minute;
                                                        if (startMinutes >= endMinutes) {
                                                            setEventEndTime(null);
                                                        }
                                                    } */
                                                }} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelCls}>Event ends on: <span className="text-red-500">*</span></label>
                                        <div className="flex flex-col gap-2 sm:flex-row">
                                            <DatePicker
                                                classNames={{ inputWrapper: fieldWrap, input: fieldText }}
                                                visibleMonths={2}
                                                endContent={<Calendar />}
                                                className="w-full"
                                                value={eventEndDate}
                                                onChange={setEventEndDate}
                                                minValue={eventStartDate || undefined}
                                            />
                                            <TimeInput
                                                classNames={{ inputWrapper: fieldWrap, input: fieldText }}
                                                endContent={<Time />}
                                                hourCycle={12}
                                                className="w-full"
                                                value={eventEndTime}
                                                minValue={eventStartTime && eventStartDate && eventEndDate && eventStartDate.compare(eventEndDate) == 0 ? eventStartTime : undefined}
                                                isInvalid={eventStartTime && eventEndTime && eventStartDate && eventEndDate && eventStartDate.compare(eventEndDate) == 0 && eventStartTime.compare(eventEndTime) >= 0}
                                                errorMessage={eventStartTime && eventEndTime && eventStartDate && eventEndDate && eventStartDate.compare(eventEndDate) == 0 && eventStartTime.compare(eventEndTime) >= 0 ? "End time must be after start time" : undefined}
                                                onChange={(newTime) => {
                                                    // If same date, ensure end time is after start time
                                                    /*  if (eventStartDate && eventEndDate && eventStartDate.toString() === eventEndDate.toString() && eventStartTime) {
                                                         if (newTime && eventStartTime) {
                                                             const startMinutes = eventStartTime.hour * 60 + eventStartTime.minute;
                                                             const endMinutes = newTime.hour * 60 + newTime.minute;
                                                             if (endMinutes <= startMinutes) {
                                                                 return; // Don't update if end time is before or equal to start time
                                                             }
                                                         }
                                                     } */
                                                    setEventEndTime(newTime);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="mb-8 border-t border-black/[0.06] pt-8 dark:border-white/10">
                                <label className={`block ${groupTitle}`}>Sales Open & Close: </label>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                    <div>
                                        <label className={labelCls}>Start on: <span className="text-red-500">*</span></label>
                                        <div className="flex flex-col gap-2 sm:flex-row">
                                            <DatePicker classNames={{ inputWrapper: fieldWrap, input: fieldText }} endContent={<Calendar />}
                                                minValue={today(getLocalTimeZone())}
                                                maxValue={eventStartDate ? eventStartDate : undefined}
                                                visibleMonths={2}
                                                className="w-full" value={salesStartDate} onChange={(newDate) => {
                                                    setSalesStartDate(newDate);
                                                    if (newDate && salesEndDate && salesEndDate.compare(newDate) < 0) {
                                                        setSalesEndDate(newDate);
                                                    }
                                                }} />
                                            <TimeInput classNames={{ inputWrapper: fieldWrap, input: fieldText }} endContent={<Time />}
                                                hourCycle={12}
                                                className="w-full" value={salesStartTime} onChange={(newTime) => {
                                                    setSalesStartTime(newTime);
                                                    // If same date and end time is now before start time, clear end time
                                                    if (salesStartDate && salesEndDate && salesStartDate.toString() === salesEndDate.toString() && newTime && salesEndTime) {
                                                        const startMinutes = newTime.hour * 60 + newTime.minute;
                                                        const endMinutes = salesEndTime.hour * 60 + salesEndTime.minute;
                                                        if (startMinutes >= endMinutes) {
                                                            setSalesEndTime(null);
                                                        }
                                                    }
                                                }} />
                                        </div>
                                    </div>
                                    <div>
                                        <label className={labelCls}>Expire on: <span className="text-red-500">*</span></label>
                                        <div className="flex flex-col gap-2 sm:flex-row">
                                            <DatePicker
                                                classNames={{ inputWrapper: fieldWrap, input: fieldText }}
                                                endContent={<Calendar />}
                                                className="w-full"
                                                visibleMonths={2}
                                                value={salesEndDate}
                                                onChange={setSalesEndDate}
                                                minValue={salesStartDate || undefined}
                                                maxValue={eventStartDate ? eventStartDate : undefined}
                                            />
                                            <TimeInput
                                                classNames={{ inputWrapper: fieldWrap, input: fieldText }}
                                                endContent={<Time />}
                                                hourCycle={12}
                                                className="w-full"
                                                value={salesEndTime}
                                                minValue={salesStartTime && salesStartDate && salesEndDate && salesStartDate.compare(salesEndDate) == 0 ? salesStartTime : undefined}
                                                isInvalid={salesStartTime && salesEndTime && salesStartDate && salesEndDate && salesStartDate.compare(salesEndDate) == 0 && salesStartTime.compare(salesEndTime) >= 0}
                                                errorMessage={salesStartTime && salesEndTime && salesStartDate && salesEndDate && salesStartDate.compare(salesEndDate) == 0 && salesStartTime.compare(salesEndTime) >= 0 ? "End time must be after start time" : undefined}
                                                onChange={(newTime) => {
                                                    // If same date, ensure end time is after start time
                                                    /* if (salesStartDate && salesEndDate && salesStartDate.toString() === salesEndDate.toString() && salesStartTime) {
                                                        if (newTime && salesStartTime) {
                                                            const startMinutes = salesStartTime.hour * 60 + salesStartTime.minute;
                                                            const endMinutes = newTime.hour * 60 + newTime.minute;
                                                            if (endMinutes <= startMinutes) {
                                                                return; // Don't update if end time is before or equal to start time
                                                            }
                                                        }
                                                    } */
                                                    setSalesEndTime(newTime);
                                                }}
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                            {validateFields() != '' && <div className="mb-4 rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">{validateFields()}</div>}
                            {error && <div className="mb-4 rounded-2xl bg-red-50 px-4 py-3 text-sm font-medium text-red-600 dark:bg-red-500/10 dark:text-red-400">{error}</div>}
                            {success && <div className="mb-4 rounded-2xl bg-green-50 px-4 py-3 text-sm font-medium text-green-700 dark:bg-green-500/10 dark:text-green-400">Event created successfully!</div>}
                            <div className="mt-8 flex justify-between gap-3">
                                <Button className={`${btnGhost} bg-transparent`} onClick={() => setStep(0)} disabled={mutation.isPending}>
                                    <span className="block text-[15px] font-bold sm:hidden">Back</span>
                                    <span className="hidden text-[15px] font-bold sm:block">&lt; Back</span>
                                </Button>
                                <Button
                                    className={`${btnAccent} ${mutation.isPending
                                        ? 'cursor-not-allowed !bg-grey80 dark:!bg-white/20'
                                        : ''
                                        }`}
                                    isLoading={mutation.isPending}
                                    onPress={handlePublish}
                                    isDisabled={mutation.isPending || validateFields() != ''}
                                >
                                    {mutation.isPending ? (
                                        <>
                                            <span className="block text-[15px] font-bold sm:hidden">Publishing...</span>
                                            <span className="hidden text-[15px] font-bold sm:block">Publishing Event...</span>
                                        </>
                                    ) : (
                                        <>
                                            <span className="block text-[15px] font-bold sm:hidden">Publish</span>
                                            <span className="hidden text-[15px] font-bold sm:block">Publish Event</span>
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    )}
                </div>
                <Footer />
                </div>
            </>
        </AuthOnly>
    );
} 